import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getUsage, buyAddon } from "../services/usage";
import { errorMessage } from "../services";
import { payPurchase } from "../utils/razorpayCheckout";
import { bytes } from "../utils/format";
import { toast } from "../utils/toast";
import Modal from "./Modal";
import { Spinner } from "./Loader";

// Store owners' limits (backend services/limits.js): image storage for all their stores, and each website's emails.
// The Overview shows every bar; from 70% a warning with "Buy more" also shows at the top of every page. A warning
// can be closed, and comes back the next day while the limit is still 70% or more used.
const CHANGED = "oz:usage-changed";
const HIDE_KEY = "oz_limit_hidden";
const today = () => new Date().toLocaleDateString("en-CA");

// Loads the owner's usage, and again after an add-on is bought anywhere in the panel
export function useUsage() {
  const [usage, setUsage] = useState(null);
  const load = useCallback(() => getUsage().then(setUsage).catch(() => setUsage(false)), []);
  useEffect(() => {
    load();
    window.addEventListener(CHANGED, load);
    return () => window.removeEventListener(CHANGED, load);
  }, [load]);
  return usage;
}

// "1 Dec" from "2026-12-01"
const resetDay = (date) => new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

// Every limit as one list: { key, label, used, limit, share, text, addon }
function limitsOf(usage) {
  if (!usage) return [];
  const storage = usage.storage;
  return [
    {
      key: "storage",
      label: "Image storage",
      hint: "Shared by all your websites",
      used: storage.usedBytes,
      limit: storage.limitBytes,
      text: `${bytes(storage.usedBytes)} of ${bytes(storage.limitBytes)}`,
      addon: { kind: "storage" },
    },
    ...usage.websites.map((site) => ({
      key: `email:${site.id}`,
      label: `Emails this month · ${site.name}`,
      hint: `${site.monthlyEmails.toLocaleString("en-IN")} a month, back to 0 on ${resetDay(usage.emailsResetOn)}${site.extraEmails ? `, plus ${site.extraEmails.toLocaleString("en-IN")} bought emails left` : ""}. Login codes, order confirmations and other emails this website sends.`,
      used: site.emailsSent,
      limit: site.emailLimit,
      text: `${site.emailsSent.toLocaleString("en-IN")} of ${site.emailLimit.toLocaleString("en-IN")} emails this month`,
      addon: { kind: "email", site: site.id, siteName: site.name },
    })),
  ].map((l) => ({ ...l, share: l.limit ? l.used / l.limit : 0 }));
}

const levelOf = (share, warnAt) => (share >= 1 ? "is-full" : share >= warnAt ? "is-high" : "");

function Bar({ share, warnAt }) {
  return (
    <div className={`usage-track ${levelOf(share, warnAt)}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(Math.min(1, share) * 100)}>
      <span style={{ width: `${share > 0 ? Math.max(1.5, Math.min(100, share * 100)) : 0}%` }} />
    </div>
  );
}

// Buying an add-on: how many, the price, then Razorpay Checkout
function AddonModal({ addon, usage, onClose }) {
  const [units, setUnits] = useState(1);
  const [busy, setBusy] = useState(false);
  const info = usage.addons[addon.kind];
  const storage = addon.kind === "storage";
  const max = usage.addons.maxUnits;
  const what = storage ? `${units} GB more storage` : `${(units * 1000).toLocaleString("en-IN")} more emails for ${addon.siteName}`;

  const buy = async () => {
    setBusy(true);
    try {
      const data = await buyAddon({ kind: addon.kind, units, site: addon.site });
      if (data.checkout) {
        const status = await payPurchase(data.checkout);
        if (status === "paid") toast(`Paid. You now have ${what}.`);
        else if (status === "pending") toast("Payment received. It will be applied in a moment.", "warn");
        else return setBusy(false); // window closed without paying
      } else if (data.usage) {
        toast(data.message);
      } else {
        toast(data.message, "warn");
        return setBusy(false);
      }
      window.dispatchEvent(new Event(CHANGED));
      onClose();
    } catch (err) {
      toast(errorMessage(err), "error");
      setBusy(false);
    }
  };

  return createPortal(
    <Modal eyebrow="ADD-ON" title={storage ? "Buy more storage" : "Buy more emails"} description={storage ? "Extra image storage for all your websites." : `Extra emails for ${addon.siteName}.`} onClose={onClose}>
      <div className="addon-body">
        <div className="addon-price">₹{info.price} <span>per {info.unit}</span></div>
        <div className="addon-qty" role="group" aria-label="How many">
          <button type="button" onClick={() => setUnits((n) => Math.max(1, n - 1))} disabled={units <= 1} aria-label="One less">−</button>
          <b>{storage ? `${units} GB` : `${(units * 1000).toLocaleString("en-IN")} emails`}</b>
          <button type="button" onClick={() => setUnits((n) => Math.min(max, n + 1))} disabled={units >= max} aria-label="One more">+</button>
        </div>
        <div className="addon-total"><span>Total</span><b>₹{(info.price * units).toLocaleString("en-IN")}</b></div>
        <p className="field-help">
          GST is added if it applies. Pay by UPI; the extra {storage ? "storage is" : "emails are"} added as soon as the payment is confirmed
          {storage ? "." : ". Bought emails never expire: they are used once the website's monthly emails run out, and what is left carries over to the next month."}
        </p>
      </div>
      <div className="product-modal-actions">
        <button type="button" className="outline-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="primary-btn" onClick={buy} disabled={busy}>{busy ? <><Spinner /> Opening payment</> : `Buy for ₹${(info.price * units).toLocaleString("en-IN")}`}</button>
      </div>
    </Modal>,
    document.body
  );
}

// The Overview card: shown only while a limit is 70% used or more, with every bar and "Buy more" for each (the bars
// are always in the website editor's Store settings)
export function UsageCard() {
  const usage = useUsage();
  const [buying, setBuying] = useState(null);
  if (!usage) return null;
  const warnAt = usage.warnAt;
  if (!limitsOf(usage).some((l) => l.share >= warnAt)) return null;
  return (
    <section className="panel usage-card tab-gap">
      <div className="panel-heading"><div><h2>Your limits</h2><p>Storage for all your websites, and emails for each website. Buy an add-on any time.</p></div></div>
      <div className="usage-rows">
        {limitsOf(usage).map((l) => (
          <div className={`usage-row ${levelOf(l.share, warnAt)}`} key={l.key}>
            <div className="usage-row-top">
              <div><b>{l.label}</b><small>{l.hint}</small></div>
              <span className="usage-figure">{l.text}<em>{Math.floor(l.share * 100)}%</em></span>
            </div>
            <Bar share={l.share} warnAt={warnAt} />
            <div className="usage-row-foot">
              {l.share >= 1 ? <span className="usage-msg">Limit reached. Buy an add-on to keep going.</span> : l.share >= warnAt ? <span className="usage-msg">Limit about to be reached. Please buy an add-on.</span> : <span />}
              <button type="button" className="outline-btn" onClick={() => setBuying(l.addon)}>＋ Buy more · ₹{usage.addons[l.addon.kind].price}/{usage.addons[l.addon.kind].unit}</button>
            </div>
          </div>
        ))}
      </div>
      {buying && <AddonModal addon={buying} usage={usage} onClose={() => setBuying(null)} />}
    </section>
  );
}

const readHidden = () => {
  try { return JSON.parse(localStorage.getItem(HIDE_KEY) || "{}"); } catch { return {}; }
};

// The warnings at the top of every page, from 70% used; closed ones come back the next day
export function LimitWarnings() {
  const usage = useUsage();
  const [hidden, setHidden] = useState(readHidden);
  const [buying, setBuying] = useState(null);
  if (!usage) return null;
  const warnings = limitsOf(usage).filter((l) => l.share >= usage.warnAt && hidden[l.key] !== today());
  if (!warnings.length) return buying ? <AddonModal addon={buying} usage={usage} onClose={() => setBuying(null)} /> : null;
  const hide = (key) => {
    const next = { ...readHidden(), [key]: today() };
    try { localStorage.setItem(HIDE_KEY, JSON.stringify(next)); } catch { /* still hidden until the page reloads */ }
    setHidden(next);
  };
  return (
    <div className="limit-warnings">
      {warnings.map((l) => (
        <div className={`limit-warning ${l.share >= 1 ? "is-full" : ""}`} key={l.key} role="status">
          <span className="limit-warning-icon" aria-hidden="true">{l.share >= 1 ? "⛔" : "⚠️"}</span>
          <div className="limit-warning-text">
            <b>{l.share >= 1 ? `${l.label}: limit reached` : `${l.label}: ${Math.floor(l.share * 100)}% used`}</b>
            <span>{l.text}. {l.share >= 1 ? "Buy an add-on to keep going." : "Limit about to be reached. Please buy an add-on."}</span>
            <Bar share={l.share} warnAt={usage.warnAt} />
          </div>
          <button type="button" className="primary-btn" onClick={() => setBuying(l.addon)}>Buy add-on</button>
          <button type="button" className="limit-warning-close" onClick={() => hide(l.key)} aria-label="Hide until tomorrow" title="Hide until tomorrow">✕</button>
        </div>
      ))}
      {buying && <AddonModal addon={buying} usage={usage} onClose={() => setBuying(null)} />}
    </div>
  );
}
